import React, { useState, useEffect, useRef, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  MessageCircle, Calendar, Globe, Star, Play, Camera,
  TrendingUp, GraduationCap, Building2, Printer, Package,
  Linkedin, ArrowRight, ChevronDown
} from "lucide-react";

const SITE = process.env.REACT_APP_BACKEND_URL;
const PUZZLE_IMG = "https://static.prod-images.emergentagent.com/jobs/9aee3f9a-9ba9-47c5-9ef1-87f76cda4250/images/fb7afdb376edc9f259896097cd88bc221876c0a7b7d59bd610713c909a009c00.png";
const GOOGLE_COLORS = ["#4285F4", "#EA4335", "#FBBC04", "#34A853", "#FF6B35"];

const vibrate = (p) => { try { navigator?.vibrate?.(p || [30, 15, 30]); } catch {} };
const strongVibrate = () => { try { navigator?.vibrate?.([40, 20, 60, 20, 40]); } catch {} };

/* ── Particles ── */
const PARTICLES = Array.from({ length: 24 }, (_, i) => {
  const a = (i / 24) * Math.PI * 2;
  const d = 60 + Math.random() * 140;
  return { tx: Math.cos(a) * d, ty: Math.sin(a) * d, size: 3 + Math.random() * 7, color: GOOGLE_COLORS[i % 5], delay: Math.random() * 0.12, dur: 0.4 + Math.random() * 0.3 };
});

function Explosion({ active }) {
  if (!active) return null;
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-50">
      {PARTICLES.map((p, i) => (
        <span key={i} className="absolute rounded-full" style={{
          width: p.size, height: p.size, backgroundColor: p.color,
          animation: `sc-burst ${p.dur}s ${p.delay}s ease-out forwards`, opacity: 0,
          "--tx": `${p.tx}px`, "--ty": `${p.ty}px`,
        }} />
      ))}
    </div>
  );
}

/* ── Vibration on scroll ── */
function VT({ children, className }) {
  const ref = useRef(null);
  const f = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting && !f.current) { f.current = true; strongVibrate(); } }, { threshold: 0.6 });
    o.observe(el);
    return () => o.disconnect();
  }, []);
  return <span ref={ref} className={className}>{children}</span>;
}

/* ══════════════════════════════════════════
   STAGE 1 — "Sos Business Profile" auto-implodes into "schedabusiness"
   ══════════════════════════════════════════ */
function LogoStage({ onTransition, ready }) {
  const [entered, setEntered] = useState(false);
  const [imploded, setImploded] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setEntered(true));
    // Auto-implode after 2s
    const t = setTimeout(() => setImploded(true), 2000);
    return () => clearTimeout(t);
  }, []);

  /* Collapsed letter style — shrinks slowly (1.2s) */
  const collapse = (delay = 0) => ({
    display: "inline-block",
    maxWidth: imploded ? 0 : "300px",
    opacity: imploded ? 0 : 0.35,
    overflow: "hidden",
    whiteSpace: "nowrap",
    verticalAlign: "bottom",
    transition: `max-width 1.4s ${delay}s cubic-bezier(0.4,0,0.2,1), opacity 1s ${delay}s ease`,
  });

  /* First letter — always colored from the start */
  const initial = (color, delay = 0) => ({
    color: color,
    transition: `text-shadow 0.8s ${delay}s ease`,
    textShadow: imploded ? `0 0 20px ${color}40, 0 0 40px ${color}20` : "none",
  });

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-6 select-none">
      <div
        className="cursor-pointer relative"
        onClick={onTransition}
        data-testid="scopri-logo"
      >
        {/* Glow — appears after implosion */}
        <div className="absolute -inset-8 rounded-full transition-all duration-1000"
          style={{
            background: imploded ? "radial-gradient(circle, rgba(255,107,53,0.15) 0%, rgba(66,133,244,0.08) 50%, transparent 70%)" : "transparent",
            transform: imploded ? "scale(1.15)" : "scale(1)",
          }}
        />

        {/* "Sos Business Profile" → "schedabusiness" */}
        <div className="relative text-center" style={{
          opacity: entered ? 1 : 0, transform: entered ? "translateY(0)" : "translateY(20px)",
          transition: "all 0.6s cubic-bezier(0.25,0.46,0.45,0.94)",
        }}>
          <div className="text-3xl sm:text-5xl md:text-7xl font-black font-google leading-none tracking-tight flex items-baseline justify-center gap-0">
            {/* S-os */}
            <span style={initial("#EA4335", 0)}>S</span>
            <span style={collapse(0)}>os</span>

            <span style={{ display: "inline-block", width: imploded ? "0px" : "8px", transition: "width 1.2s 0.1s ease" }} />

            {/* B-usiness */}
            <span style={initial("#FBBC04", 0.1)}>B</span>
            <span style={collapse(0.1)}>usiness</span>

            <span style={{ display: "inline-block", width: imploded ? "0px" : "8px", transition: "width 1.2s 0.2s ease" }} />

            {/* P-rofile */}
            <span style={initial("#34A853", 0.2)}>P</span>
            <span style={collapse(0.2)}>rofile</span>
          </div>

          {/* Subtitle — fades out on implosion */}
          <p className="text-xs md:text-sm text-white/30 mt-3 font-google tracking-wide" style={{
            opacity: imploded ? 0 : (entered ? 1 : 0),
            transform: imploded ? "translateY(-8px)" : "translateY(0)",
            maxHeight: imploded ? 0 : "40px",
            transition: "all 0.8s ease",
          }}>
            Problemi con la tua scheda Google?
          </p>

          {/* Google Maps Pin — appears after implosion */}
          <div className="flex justify-center" style={{
            opacity: imploded ? 1 : 0,
            transform: imploded ? "translateY(0) scale(1)" : "translateY(-10px) scale(0.5)",
            marginTop: imploded ? "8px" : "0px",
            maxHeight: imploded ? "40px" : "0px",
            transition: "all 0.8s 0.6s ease",
          }}>
            <svg width="28" height="28" viewBox="0 0 48 48" fill="none" className="drop-shadow-lg">
              <path d="M24 4C15.163 4 8 11.163 8 20C8 31 24 44 24 44C24 44 40 31 40 20C40 11.163 32.837 4 24 4Z" fill="#4285F4"/>
              <circle cx="24" cy="20" r="8" fill="white"/>
              <circle cx="24" cy="20" r="4" fill="#4285F4"/>
            </svg>
          </div>
        </div>
      </div>

      {/* Spinoff labels */}
      <div className="flex items-center gap-8 mt-8" style={{
        opacity: entered ? 1 : 0, transform: entered ? "translateY(0)" : "translateY(12px)",
        transition: "all 0.5s 0.15s ease",
      }}>
        <span className="text-xs font-black font-google tracking-wide text-center" style={{
          color: imploded ? "#ff6b35" : "rgba(255,255,255,0.15)",
          transition: "color 1s 0.3s ease",
        }}>A chi ti rivolgi quando hai un problema su Google?</span>
      </div>

      {/* Tagline — effetto ONDA lettere */}
      <div className="mt-10 text-sm tracking-[0.3em] uppercase font-google relative text-center" style={{
        opacity: entered ? 1 : 0,
        transform: entered ? "translateY(0)" : "translateY(10px)",
        transition: "all 0.5s ease",
        transitionDelay: "0.25s",
      }}>
        {"no bot...just solutions".split("").map((ch, i) => (
          <span key={i} className="inline-block" style={{
            color: ch === "." ? "#ff6b35" : (imploded ? "#ffffff" : "rgba(255,255,255,0.18)"),
            textShadow: imploded ? "0 0 8px rgba(255,255,255,0.4)" : "none",
            animation: imploded ? `sc-wave 1.8s ${i * 0.06}s ease-in-out infinite` : "none",
            minWidth: ch === " " ? "0.3em" : undefined,
            transition: "color 0.5s ease",
          }}>{ch}</span>
        ))}
      </div>

      {/* Hint */}
      <div className="mt-14 animate-bounce" style={{ opacity: imploded ? 0.5 : 0, transition: "opacity 1s 1s" }}>
        <ChevronDown className="w-5 h-5 text-gray-500" />
      </div>

      {!ready && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <div className="w-1 h-1 rounded-full bg-[#ff6b35]/40 animate-ping" />
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════
   STAGE 2 — Puzzle (preloaded)
   ══════════════════════════ */
function PuzzleStage({ onTransition }) {
  const [v, setV] = useState(false);
  useEffect(() => { requestAnimationFrame(() => setV(true)); }, []);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-6 select-none">
      <div className="cursor-pointer" onClick={onTransition} data-testid="scopri-puzzle"
        style={{ transform: v ? "scale(1)" : "scale(0.5)", opacity: v ? 1 : 0, transition: "all 0.5s cubic-bezier(0.34,1.4,0.64,1)" }}>
        <img src={PUZZLE_IMG} alt="Street View + schedabusiness = Problema Risolto" className="w-[85vw] max-w-lg rounded-2xl shadow-2xl" loading="eager"
          style={{ boxShadow: "0 0 60px rgba(255,107,53,0.12)" }} />
      </div>
      <div style={{ opacity: v ? 1 : 0, transform: v ? "translateY(0)" : "translateY(16px)", transition: "all 0.4s 0.25s ease" }}>
        <p className="mt-5 text-white text-xl md:text-2xl font-black font-google text-center">
          Il tassello <span className="text-[#ff6b35]">mancante</span>
        </p>
        <p className="text-gray-500 text-xs text-center mt-1">Street View + schedabusiness = Problema Risolto</p>
      </div>
      <div className="mt-8 animate-bounce"><ChevronDown className="w-5 h-5 text-gray-700" /></div>
    </div>
  );
}

/* ══════════════════════════
   STAGE 3 — Full Menu
   ══════════════════════════ */
function MenuStage() {
  const s = SITE;
  const [vi, setVi] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setVi(v => { if (v >= 25) { clearInterval(t); return v; } return v + 1; }), 50);
    return () => clearInterval(t);
  }, []);
  const is = (i) => ({ opacity: vi > i ? 1 : 0, transform: vi > i ? "translateY(0)" : "translateY(20px)", transition: "all 0.35s ease" });

  return (
    <div className="flex flex-col items-center px-4 py-8 pb-16 w-full">

      <div className="text-center mb-5" style={is(0)}>
        <div className="text-3xl font-black font-google mb-1">
          <span className="text-[#EA4335]">S</span><span className="text-[#FBBC04]">B</span><span className="text-[#34A853]">P</span>
          <span className="text-white text-lg ml-1">Hub</span>
        </div>
        <p className="text-gray-500 text-[10px] tracking-widest uppercase">no bot...just solutions</p>
      </div>

      <div className="w-full max-w-md mb-4" style={is(1)}>
        <div className="bg-gradient-to-r from-[#ff6b35]/10 to-transparent border-l-2 border-[#ff6b35] rounded-r-xl px-4 py-3">
          <VT className="text-white/80 text-sm font-bold font-google">Dai una scossa al tuo Business Profile</VT>
        </div>
      </div>

      <div className="w-full max-w-md space-y-2 mb-4">
        {[
          { href: "https://wa.me/+393273839782", icon: MessageCircle, ic: "text-[#25D366]", bg: "bg-[#25D366]/15 border-[#25D366]/30 hover:border-[#25D366]/60", name: "WhatsApp", sub: "+39 327 383 9782", idx: 2 },
          { href: "https://calendly.com/riccardorossi1984", icon: Calendar, ic: "text-blue-400", bg: "bg-white/10 border-white/10 hover:border-white/20", name: "Prenota Videochiamata", sub: "Calendly", idx: 3 },
          { href: s, icon: Globe, ic: "text-[#ff6b35]", bg: "bg-white/10 border-white/10 hover:border-white/20", name: "Sito Web schedabusiness", badge: "VISITA", idx: 4 },
        ].map(l => (
          <div key={l.name} style={is(l.idx)}>
            <a href={l.href} target="_blank" rel="noopener noreferrer"
              className={`block w-full ${l.bg} border rounded-2xl px-4 py-3 text-white transition-all`} data-testid={`scopri-${l.name.toLowerCase().replace(/\s/g,'-')}`}>
              <div className="flex items-center gap-3">
                <l.icon className={`w-5 h-5 ${l.ic}`} />
                <span className="font-bold text-sm flex-1">{l.name}</span>
                {l.badge ? <span className="bg-[#ff6b35] text-white text-[10px] px-2 py-0.5 rounded-full font-bold">{l.badge}</span>
                  : <span className="text-gray-400 text-xs">{l.sub}</span>}
              </div>
            </a>
          </div>
        ))}
      </div>

      <div className="w-full max-w-md mb-4" style={is(5)}>
        <div className="text-center py-1.5">
          <VT className="text-[#ff6b35] text-xs font-black uppercase tracking-widest">Turbo · Boost · Al tuo profilo</VT>
        </div>
      </div>

      <div className="w-full max-w-md space-y-2 mb-4">
        {[
          { href: "/deducibile100", icon: GraduationCap, c: "text-[#ff6b35]", bg: "bg-gradient-to-r from-[#5C1A1B]/40 to-[#8B4513]/40 border-[#7B2D26]/50", name: "Deducibile 100%", sub: "9 anni · 7K foto · 52M views", price: "", tag: "PRENOTA", idx: 6 },
          { href: "/start", icon: Play, c: "text-blue-400", bg: "bg-blue-500/15 border-blue-500/30", name: "Start", sub: "#DignitaDigitale", price: "€99", tag: "€33 x3", idx: 7 },
          { href: "/plus", icon: Star, c: "text-red-400", bg: "bg-red-500/15 border-red-500/30", name: "Plus", sub: "Il preferito", price: "€299", tag: "IL PIU' VENDUTO", idx: 8 },
          { href: "/rooftop", icon: TrendingUp, c: "text-amber-400", bg: "bg-amber-500/15 border-amber-500/30", name: "Rooftop", sub: "TUTTO + Assistenza 12 mesi", price: "€599", tag: "€49,91/mese", idx: 9 },
          { href: "/deducibile100", icon: Camera, c: "text-teal-400", bg: "bg-teal-500/15 border-teal-500/30", name: "Virtual Tour 360°", sub: "Virtual Tour GoPro MAX", price: "€499", tag: "START INCLUSO", tw: true, idx: 10 },
        ].map(p => (
          <div key={p.href} style={is(p.idx)}>
            <a href={`${s}${p.href}`} target="_blank" rel="noopener noreferrer"
              className={`block w-full ${p.bg} border rounded-2xl px-4 py-3 text-white transition-all hover:opacity-90`} data-testid={`scopri-${p.href.slice(1)}`}>
              <div className="flex items-center gap-3">
                <p.icon className={`w-5 h-5 ${p.c}`} />
                <div className="flex-1">
                  <span className={`font-bold text-sm ${p.tw ? "font-typewriter" : ""}`}>{p.name}</span>
                  <p className="text-[10px] text-white/50">{p.sub}</p>
                </div>
                <div className="text-right">
                  <span className="font-black text-sm">{p.price}</span>
                  <p className="text-[9px] text-gray-400">{p.tag}</p>
                </div>
              </div>
            </a>
          </div>
        ))}
      </div>

      <div className="w-full max-w-md mb-4" style={is(11)}>
        <div className="bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-center">
          <VT className="text-white/70 text-sm italic leading-relaxed font-google">
            Fatti trovare dalle persone che cercano il tuo servizio, ma non ti conoscono ancora
          </VT>
        </div>
      </div>

      <div className="w-full max-w-md space-y-2 mb-4">
        {[
          { href: `${s}/enti`, icon: Building2, c: "text-green-400", bg: "bg-green-500/10 border-green-500/30", name: "Convenzione Enti — Opzione C", sub: "50/50: Deducibile 100%", idx: 12 },
          { href: `${s}/per-agenzie`, icon: Printer, c: "text-[#ff6b35]", bg: "bg-[#ff6b35]/15 border-[#ff6b35]/30 hover:border-[#ff6b35]/60", name: "Brand Kit per Agenzie", sub: "Loghi, colori, font + ZIP", badge: "APRI", idx: 13 },
          { href: `${s}/merchandising`, icon: Package, c: "text-amber-400", bg: "bg-white/5 border-white/10 hover:border-white/20", name: "Merchandising schedabusiness", sub: "2 collezioni, 5 prodotti", idx: 14 },
          { href: "https://www.linkedin.com/in/riccardorossi1984googletnx/", icon: Linkedin, c: "text-[#0077B5]", bg: "bg-[#0077B5]/20 border-[#0077B5]/30 hover:border-[#0077B5]/60", name: "LinkedIn", badge: "PREMIUM", badgeC: "bg-[#D4AF37] text-gray-900", idx: 15 },
        ].map(l => (
          <div key={l.name} style={is(l.idx)}>
            <a href={l.href} target="_blank" rel="noopener noreferrer"
              className={`block w-full ${l.bg} border rounded-2xl px-4 py-3 text-white transition-all`}
              data-testid={`scopri-${l.name.toLowerCase().replace(/\s/g,'-')}`}>
              <div className="flex items-center gap-3">
                <l.icon className={`w-5 h-5 ${l.c}`} />
                <div className="flex-1"><span className="font-bold text-sm">{l.name}</span><p className="text-[10px] text-white/50">{l.sub}</p></div>
                {l.badge && <span className={`${l.badgeC || "bg-[#ff6b35] text-white"} text-[10px] px-2 py-0.5 rounded-full font-bold`}>{l.badge}</span>}
                {!l.badge && <ArrowRight className="w-4 h-4 text-gray-600" />}
              </div>
            </a>
          </div>
        ))}
      </div>

      <div className="text-center mb-5" style={is(16)}>
        <p className="text-gray-600 text-[10px] mb-2 uppercase tracking-wider">Scansiona e condividi</p>
        <div className="inline-block bg-white rounded-2xl p-4 shadow-xl relative" data-testid="scopri-qr">
          <QRCodeSVG value={`${s}/scopri`} size={120} fgColor="#1a1a2e" bgColor="#ffffff" level="M" />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-white rounded-full p-1.5 shadow">
              <svg className="w-5 h-5" viewBox="0 0 48 48"><path d="M24 4C15.163 4 8 11.163 8 20C8 31 24 44 24 44C24 44 40 31 40 20C40 11.163 32.837 4 24 4Z" fill="#4285F4"/><circle cx="24" cy="20" r="8" fill="white"/><circle cx="24" cy="20" r="4" fill="#4285F4"/></svg>
            </div>
          </div>
        </div>
        <p className="text-gray-700 text-[9px] mt-2">{s}/scopri</p>
      </div>

      <div className="text-center" style={is(17)}>
        <p className="font-black text-sm font-google">
          <span className="text-[#EA4335]">S</span><span className="text-[#FBBC04]">B</span><span className="text-[#34A853]">P</span>
          <span className="text-white"> — Business Profile Solutions</span>
        </p>
        <p className="text-gray-600 text-[10px] mt-1 font-google"><strong>Made in Toscana</strong>, <strong>Italy</strong></p>
      </div>
    </div>
  );
}

/* ══════════════════════════
   MAIN — Preloads Pz in background
   ══════════════════════════ */
export default function ScopriPage() {
  const [stage, setStage] = useState(1);
  const [exploding, setExploding] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [pzReady, setPzReady] = useState(false);

  // Preload Pz image silently while user sees Stage 1
  useEffect(() => {
    const img = new Image();
    img.onload = () => setPzReady(true);
    img.src = PUZZLE_IMG;
  }, []);

  const goToStage = useCallback((next) => {
    if (transitioning) return;
    if (next === 2 && !pzReady) return; // Don't transition if image not loaded
    setTransitioning(true);
    setExploding(true);
    vibrate([40, 20, 60, 20, 40]);
    setTimeout(() => { setStage(next); setExploding(false); setTransitioning(false); }, 450);
  }, [transitioning, pzReady]);

  return (
    <div className="min-h-screen bg-[#0c1220] text-white overflow-x-hidden relative" data-testid="scopri-page">
      <style>{`
        @keyframes sc-burst {
          0% { transform: translate(0,0) scale(1); opacity:1; }
          100% { transform: translate(var(--tx),var(--ty)) scale(0); opacity:0; }
        }
        @keyframes sc-wave {
          0%, 100% { transform: translateY(0) scale(1); opacity: 0.8; }
          30% { transform: translateY(-6px) scale(1.15); opacity: 1; text-shadow: 0 0 12px rgba(255,255,255,0.9); }
          60% { transform: translateY(1px) scale(0.98); opacity: 0.9; }
        }
        @keyframes sc-shine {
          0% { left: -120%; }
          50% { left: 120%; }
          100% { left: 120%; }
        }
        .sc-shine {
          background: linear-gradient(90deg, transparent 0%, transparent 20%, rgba(255,255,255,0.05) 30%, rgba(255,255,255,0.95) 48%, rgba(255,255,255,1) 50%, rgba(255,255,255,0.95) 52%, rgba(255,255,255,0.05) 70%, transparent 80%, transparent 100%);
          width: 80%;
          animation: sc-shine 2.5s ease-in-out infinite;
          mix-blend-mode: overlay;
        }
      `}</style>

      <Explosion active={exploding} />

      <div style={{
        opacity: exploding ? 0.2 : 1,
        transform: exploding ? "scale(0.92)" : "scale(1)",
        transition: "all 0.25s ease",
      }}>
        {stage === 1 && <LogoStage onTransition={() => goToStage(2)} ready={pzReady} />}
        {stage === 2 && <PuzzleStage onTransition={() => goToStage(3)} />}
        {stage === 3 && <MenuStage />}
      </div>
    </div>
  );
}
